import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  ModalFooter,
  Text,
  Flex,
  Input,
  Select,
  Checkbox,
  Box,
  Link,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import useSignup from './useSignup';
import { countries, softwareLanguages } from './constants/data';
import { safeGet, safeSet } from './utils/safeStorage';
import useThemeMode, { useThemeTokens } from './hooks/useThemeMode';

const getCountryFromLocale = () => {
  const locale = Intl.DateTimeFormat().resolvedOptions().locale;
  const country = locale.split('-')[1]; // The part after the dash is usually the country code
  // OS-derived 'US' is frequently a wrong default; force a manual pick instead of auto-selecting.
  if (!country || country === 'US') return '';
  return country;
};

// Privacy policy URLs for different languages
const getPrivacyPolicyUrl = (lang) => {
  const urls = {
    en: 'https://calibrite.com/privacy-policy/',
    es: 'https://calibrite.com/es/privacy-policy/?noredirect=es-ES',
    fr: 'https://calibrite.com/fr/privacy-policy/?noredirect=fr-FR',
    de: 'https://calibrite.com/de/privacy-policy/?noredirect=de-DE',
    it: 'https://calibrite.com/it/privacy-policy/?noredirect=it-IT',
    pl: 'https://calibrite.com/pl/privacy-policy/?noredirect=pl-PL',
    ja: 'https://calibrite.com/jp/privacy-policy/?noredirect=ja-JP',
  };
  return urls[lang] || urls.en; // Default to English if language not found
};

// Terms of Use URLs for different languages
const getTermsOfUseUrl = (lang) => {
  const urls = {
    en: 'https://calibrite.com/terms-of-use/',
    es: 'https://calibrite.com/es/terms-of-use/?noredirect=es-ES',
    fr: 'https://calibrite.com/fr/terms-of-use/?noredirect=fr-FR',
    de: 'https://calibrite.com/de/terms-of-use/?noredirect=de-DE',
    it: 'https://calibrite.com/it/terms-of-use/?noredirect=it-IT',
    pl: 'https://calibrite.com/pl/terms-of-use/?noredirect=pl-PL',
    ja: 'https://calibrite.com/jp/terms-of-use/?noredirect=ja-JP',
  };
  return urls[lang] || urls.en; // Default to English if language not found
};

export const DetailsStage = ({
  onSubmit,
  email,
  password,
  onBack,
  apiUrl,
  newsletter,
  openSoftwareLicense,
  softwareLicenseVersion,
}) => {
  const { t, i18n } = useTranslation();
  // Light theme: consent text uses the muted #595959. Dark inherits the card text.
  const light = useThemeMode();
  const tokens = useThemeTokens();
  const {
    register,
    handleSubmit,
    watch,
    unregister,
    formState: { errors },
  } = useForm({
    defaultValues: {
      contactConsent: true,
      country: getCountryFromLocale(),
    },
  });

  const formData = watch();

  const { signup, loading, error } = useSignup(apiUrl, softwareLicenseVersion);

  const isMarketingSignup = safeGet('isMarketingSignup') === 'true';

  const onSubmitForm = useCallback(
    async (data) => {
      const fullData = {
        ...data,
        email,
        password,
      };

      safeSet('emailOptIn', data.contactConsent);

      const response = await signup(fullData, isMarketingSignup, newsletter);
      if (response.user) {
        console.log('User created successfully');
        onSubmit({
          password,
          alreadyRegistered: response.alreadyRegistered,
        });
      }
    },
    [signup, email, password, isMarketingSignup, onSubmit, newsletter],
  );

  // Get the current language for URLs
  const currentLang = i18n.language.split('-')[0];
  const privacyPolicyUrl = getPrivacyPolicyUrl(currentLang);
  const termsOfUseUrl = getTermsOfUseUrl(currentLang);

  const isStudent = formData.marketSegment === 'student';

  const currentYear = new Date().getFullYear();
  const graduationYears = Array.from({ length: 4 }, (_, i) => currentYear + i);

  const workAreas = [
    { value: 'photography', labelKey: 'label.Photography' },
    { value: 'videography', labelKey: 'label.Videography' },
    { value: 'photography_video', labelKey: 'label.Photography and Video' },
    { value: 'content_creation', labelKey: 'label.Content Creation' },
    { value: 'design_print', labelKey: 'label.Design and Print' },
    { value: 'other', labelKey: 'label.Other' },
  ];

  React.useEffect(() => {
    if (isStudent) {
      // Student takes the graduation year; work area is not collected.
      unregister('marketWorkAreaSegment');
    } else {
      unregister('studentGraduationYear');
      unregister('studentId');
    }
  }, [isStudent, unregister]);

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <Flex direction="column" gap={4}>
        {!isMarketingSignup && (
          <>
            {/* Wrap so the two fields stack in a narrow embed frame instead of
                overflowing sideways. The 8rem basis keeps them side by side at
                the standalone width and stacks them below about 272px. */}
            <Flex direction="row" gap={4} flexWrap="wrap">
              <Box flex="1 1 8rem" minW="8rem">
                <Input
                  autoFocus
                  placeholder={t('label.First Name')}
                  {...register('firstName', { required: true })}
                />
                {errors.firstName && <Text color="red.500">{t('label.requiredField')}</Text>}
              </Box>
              <Box flex="1 1 8rem" minW="8rem">
                <Input
                  placeholder={t('label.Last Name')}
                  {...register('lastName', { required: true })}
                />
                {errors.lastName && <Text color="red.500">{t('label.requiredField')}</Text>}
              </Box>
            </Flex>
            <Select
              placeholder={t('label.Preferred Language')}
              {...register('language', { required: true })}
              defaultValue={
                softwareLanguages.find((lang) => lang.short === i18n.language)?.short || ''
              }
            >
              {softwareLanguages.map((lang) => (
                <option key={lang.short} value={lang.short}>
                  {lang.language}
                </option>
              ))}
            </Select>
            {errors.language && <Text color="red.500">{t('label.requiredField')}</Text>}
            <Select placeholder={t('label.Country')} {...register('country', { required: true })}>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </Select>
            {errors.country && <Text color="red.500">{t('label.requiredField')}</Text>}
            {/* Wrap so the two fields stack in a narrow embed frame instead of
                overflowing sideways. The 8rem basis keeps them side by side at
                the standalone width and stacks them below about 272px. */}
            <Flex direction="row" gap={4} flexWrap="wrap">
              <Box flex="1 1 8rem" minW="8rem">
                <Select
                  placeholder={t('label.marketSegment')}
                  {...register('marketSegment', { required: true })}
                >
                  <option value="business">{t('label.Business User')}</option>
                  <option value="consumer">{t('label.Enthusiast')}</option>
                  <option value="student">{t('label.Student')}</option>
                </Select>
              </Box>
              <Box flex="1 1 8rem" minW="8rem">
                {isStudent ? (
                  <Select
                    placeholder={t('label.Graduation Year')}
                    {...register('studentGraduationYear', { required: true })}
                  >
                    {graduationYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Select
                    placeholder={t('label.marketSegmentField')}
                    {...register('marketWorkAreaSegment', { required: true })}
                  >
                    {workAreas.map((area) => (
                      <option key={area.value} value={area.value}>
                        {t(area.labelKey)}
                      </option>
                    ))}
                  </Select>
                )}
              </Box>
            </Flex>
            {(errors.marketSegment ||
              errors.studentGraduationYear ||
              errors.marketWorkAreaSegment) && (
              <Text color="red.500">{t('label.requiredField')}</Text>
            )}
            {/* The mailing-list page states the full consent text that Calibrite
                legal supplies. The software page keeps the short opt-in text and
                the consent checkbox. Some translations of the consent text wrap
                it in <p>, so it renders in a Box and not in a Text — a Text is a
                <p> itself, and a <p> inside a <p> is not valid. */}
            {newsletter ? (
              // The consent text holds two links: a mailto for the newsletter
              // address, and the privacy policy. The policy URL is per language,
              // so the string takes it as {{privacyUrl}} rather than hard-coding
              // one URL in eleven translation files. The anchors come from the
              // string, so they need the link colour applied here.
              <Box
                fontSize="sm"
                color={light ? tokens.secondaryText : undefined}
                sx={{ a: { color: 'blue.500', textDecoration: 'underline' } }}
                dangerouslySetInnerHTML={{
                  __html: t('otherHtml.mailing-list-legal', {
                    privacyUrl: privacyPolicyUrl,
                  }),
                }}
              />
            ) : (
              <>
                <Text>
                  {t(
                    'label.Calibrite may keep me informed with emails about products and services. See our',
                  )}{' '}
                  <Link
                    href={privacyPolicyUrl}
                    isExternal
                    color="blue.500"
                    textDecoration="underline"
                  >
                    {t('miscNonHtmlText.Privacy Policy')}
                  </Link>{' '}
                  {t('label.for more details or to opt-out at any time.')}
                </Text>

                <Checkbox {...register('contactConsent')}>{t('label.contactConsent')}</Checkbox>
              </>
            )}
          </>
        )}
        <Text fontSize="sm" color={light ? tokens.secondaryText : undefined}>
          {newsletter
            ? t('label.By clicking the Sign Up button I agree to the')
            : t('label.By clicking the Create Account I agree to the')}{' '}
          <Link
            href={termsOfUseUrl}
            isExternal
            color="blue.500"
            textDecoration="underline"
          >
            {t('label.Terms of Use')}
          </Link>
          {!newsletter && (
            <>
              {' '}
              {t('label.and')}{' '}
              <Text
                as="span"
                color="blue.500"
                textDecoration="underline"
                cursor="pointer"
                onClick={() => openSoftwareLicense()}
              >
                {t('label.Software License Agreement')}
              </Text>{' '}
              {t('label.and')}{' '}
              <Link
                href={privacyPolicyUrl}
                isExternal
                color="blue.500"
                textDecoration="underline"
              >
                {t('miscNonHtmlText.Privacy Policy')}
              </Link>
            </>
          )}
          .
        </Text>
        {error && <Text color="red.500">{error}</Text>}
      </Flex>
      <ModalFooter justifyContent="space-between">
        <Button colorScheme="purple" onClick={onBack}>
          {t('button.Back')}
        </Button>
        {newsletter ? (
          <Button colorScheme="green" type="submit" isLoading={loading}>
            {t('button.Sign Up')}
          </Button>
        ) : (
          <Button colorScheme="green" type="submit" isLoading={loading}>
            {t('button.Create Account')}
          </Button>
        )}
      </ModalFooter>
    </form>
  );
};
